/**
 * Money, drawn as money.
 *
 * Poker published a pot from its first day and nothing on the table read it.
 * A bet was a number in the corner of a chair quietly going down; the end of a
 * hand was chips appearing in somebody's stack from nowhere. Both are the same
 * complaint the finished trick had before `TrickWon`: the game was right and
 * the table said nothing.
 *
 * ## Why a drawn chip and not an image
 *
 * A chip is a circle with a striped edge. That is within reach of a border and
 * a repeating gradient, and being drawn rather than fetched means it costs no
 * request, scales to any size, and takes the table's colours with it - a
 * painted theme gets chips that match without anybody drawing eleven more
 * sets of them.
 *
 * ## Why the amount is written on it
 *
 * Real chips carry denominations and a real player counts the stack. Neither
 * survives a phone: at this size a stack is a smudge, and the question anybody
 * actually has is "how much is in there". So the stack says how many chips are
 * in play and the label says what they are worth, which is the honest division
 * of labour between a picture and a number.
 */

/**
 * How many discs to draw for an amount.
 *
 * Not one per chip - a pot of four hundred would be four hundred circles. The
 * stack grows with the logarithm of the amount, so a big pot LOOKS bigger
 * without the drawing becoming a counting exercise nobody asked for.
 */
export function stackHeight(amount: number): number {
  if (amount <= 0) return 0;
  return Math.max(1, Math.min(5, 1 + Math.floor(Math.log10(amount))));
}

/**
 * One chip, or a short stack of them, with what it is worth.
 *
 * `amount` of zero draws nothing at all rather than an empty stack: a seat
 * that has not bet has no chips in front of it, which is the same thing a real
 * table says by having none there.
 */
export function ChipStack({
  amount,
  label,
  className,
}: {
  amount: number;
  /** A word above the number - "Pot". Absent for a seat's own bet. */
  label?: string;
  className?: string;
}) {
  if (!(amount > 0)) return null;

  const discs = stackHeight(amount);

  return (
    <div className={className === undefined ? 'chip-stack' : `chip-stack ${className}`}>
      <span className="chip-pile" aria-hidden="true">
        {Array.from({ length: discs }, (_, index) => (
          /* Stacked upwards by index, so the pile reads as depth rather than
             as several chips lying beside each other. */
          <span key={index} className="chip" style={{ '--i': String(index) } as React.CSSProperties} />
        ))}
      </span>
      <span className="chip-amount">
        {label === undefined ? null : <span className="chip-label">{label}</span>}
        {amount}
      </span>
    </div>
  );
}
